import MeshWriter, { Attr, AttrMap, MeshWriterError, TypedArray } from './MeshWriter';
import { Join } from './ops';
import {
    BufferAttribute,
    BufferGeometry,
    Group,
    LineBasicMaterial,
    LineSegments as LineSegments,
    Matrix4,
    Vector3 } from 'three';

/**
 * A ThreeJSMesh is a MeshWriter that sources mesh data from a
 * BufferGeometry instance. It also provides a static method to create a
 * BufferGeometry from generated mesh data.
 * @param {BufferGeometry} geo - The BufferGeometry instance. This is
 * converted to non-indexed form (if required), otherwise the class retains a
 * copy of the given geometry. If you want to mutate the original, be aware that
 * that mutation will be observed when `attr` or `write` are called.
 */
export class ThreeJSMesh extends MeshWriter {
    geo: BufferGeometry;

    constructor(geo: BufferGeometry) {
        super();
        if(geo.index === null) {
            this.geo = geo;
        } else {
            this.geo = geo.toNonIndexed();
        }
    }

    /**
     * This implementation of the MeshWriter `attr` interface method produces
     * values that represent the underlying BufferGeometry.
     */
    get attrs() {
        const attrSet: Set<Attr> = new Set();
        for(const attrName of Object.keys(this.geo.attributes)) {
            const tAttr = this.geo.getAttribute(attrName);
            const size = tAttr.itemSize;
            const count = tAttr.count;
            const type = Object.getPrototypeOf(tAttr.array).constructor;
            attrSet.add(new Attr(attrName, size, count, type));
        }
        return attrSet;
    }

    write(attrMap: AttrMap) {
        for(const [attr, data] of attrMap.entries()) {
            const tAttr = this.geo.getAttribute(attr.name);
            data.set(tAttr.array);
        }
    }

    /**
     * Transforms an attribute mapping, the sort of map returned from
     * MeshWriter.generate(), into a three.js BufferGeometry instance. Note that
     * this implementation currently uses the r102 API, which has changed
     * in more recent versions.
     */
    static toBufferGeometry(attrMap: AttrMap) {
        const geo = new BufferGeometry();
        for(const [attr, data] of attrMap.entries()) {
            const tAttr = new BufferAttribute(data, attr.size);
            geo.setAttribute(attr.name, tAttr);
        }
        return geo;
    }
}


/**
 * Generate an axis-aligned XY plane with its lower-left vertex at (x, y), and
 * its upper right vertex at (x+width, y+height).
 */
export class PlaneMesh extends MeshWriter {
    x: number;
    y: number;
    width: number;
    height: number;

    constructor(x: number, y: number, width: number, height: number) {
        super();
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
    }

    get attrs() {
        let result: Set<Attr> = new Set();
        // name, size, count, type
        result.add(new Attr('position', 3, 6, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const x = this.x,
              y = this.y,
              w = this.width, h = this.height,
              ax = x,       ay = y,
              bx = x+w,     by = y,
              cx = x+w,     cy = y+h,
              dx = x,       dy = y+h,
              [_, pos] = attrMap.byName('position');
        PlaneMesh.write3(pos, 0, ax, ay, 0, bx, by, 0, cx, cy, 0, dx, dy, 0);
    }

    /**
     * A convenience method to write an arbitrary quad in 3D space. This is
     * actually more general than the PlaneMesh, which is always in the XY plane
     * with a zero Z coordinate. The triangles are written counter-clocwise,
     * assuming that the vertexes are also given counterclockwise. Thus the
     * two sub-triangles are ABC and ACD. This can write position, normal, or
     * vertex color data. The equivalent for UVs is write2().
     *
     *     D +------ + C
     *       |      /|
     *       |    /  |
     *       |  /    |
     *       |/      |
     *     A +------ + B
     *
     * @param d - The array to fill with data
     * @param i - the index at which to begin writing
     * @returns - the next index one might want to write into; this
     * helps if you want to do a bunch of quads and need help keeping track. It
     * will always be i+18 though, if you don't want to use it.
     */
    static write3(d: TypedArray, i: number,
                  ax: number, ay: number, az: number,
                  bx: number, by: number, bz: number,
                  cx: number, cy: number, cz: number,
                  dx: number, dy: number, dz: number)
    {
        // write the triangle ABC
        d[i++] = ax; d[i++] = ay; d[i++] = az;
        d[i++] = bx; d[i++] = by; d[i++] = bz;
        d[i++] = cx; d[i++] = cy; d[i++] = cz;

        // Now the triangle ACD
        d[i++] = ax; d[i++] = ay; d[i++] = az;
        d[i++] = cx; d[i++] = cy; d[i++] = cz;
        d[i++] = dx; d[i++] = dy; d[i++] = dz;

        return i;
    }

    /**
     * This is equivalent to write3, but writes two coordinates per vertex,
     * appropriate for UV coordinates. See the docs for write3.
     */
    static write2(d: TypedArray, i: number,
                  au: number, av: number,
                  bu: number, bv: number,
                  cu: number, cv: number,
                  du: number, dv: number)
    {
        // Triangle ABC
        d[i++] = au; d[i++] = av;
        d[i++] = bu; d[i++] = bv;
        d[i++] = cu; d[i++] = cv;

        // Triangle ACD
        d[i++] = au; d[i++] = av;
        d[i++] = cu; d[i++] = cv;
        d[i++] = du; d[i++] = dv;

        return i;
    }
}


/**
 * Generate a 90 degree triangle fan inside a unit plane. The `n` parameter
 * specifies how many vertexes to generate along the path of the arc. `n=2` will
 * generate a degenerate arc that's really just a triangle (half a quad). Larger
 * values of `n` will better approximate the arc with less faceting.
 */
export class ArcMesh extends MeshWriter {
    n: number;

    constructor(n: number) {
        super();
        if(n < 2) throw new MeshWriterError('An ArcMesh must have n >= 2.');
        this.n = n;
    }

    get attrs() {
        const result = new Set<Attr>();
        const count = 3 * (this.n-1);
        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const n = this.n,
              nm1 = n - 1,
              inc = Math.PI / (2*(n-1)),
              [_pattr, pos] = attrMap.byName('position');

        let b = [1, 0];
        for(let i=0, j=0, k=0, t=inc; i < nm1; i++, j+=9, k+=6, t+=inc) {
            const c = [Math.cos(t), Math.sin(t)];
            pos.set([0, 0, 0, ...b, 0, ...c, 0], j);
            b = c;
        }
    }
}


export class InvArcMesh extends MeshWriter {
    n: number;

    constructor(n: number) {
        super()
        if(n < 2) throw new MeshWriterError('An InvArcMesh must have n >= 2');
        this.n = n;
    }

    get attrs() {
        const result = new Set<Attr>();
        const count = 3 * (this.n-1);
        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const n = this.n,
              nm1 = n - 1,
              inc = Math.PI / (2*nm1),
              [_pattr, pos] = attrMap.byName('position'),
              start = Math.PI + inc;

        let c = [0, 1];
        for(let i=0, j=0, k=0, t=inc; i < nm1; i++, j+=9, k+=6, t+=inc) {
            const b = [1-Math.cos(t), 1-Math.sin(t)];
            pos.set([0, 0, 0, ...b, 0, ...c, 0], j);
            c = b;
        }
    }
}


/**
 * Generates a quad with a round hole centered inside it. If n=0, the hole is
 * diamond shaped and the output has eight triangles. If n=1, then the hole is
 * octagonal because each quarter is cut once. If n=2, each quarter is cut twice
 * so that the hole has 12 vertexes. So the general formula for vertexes around
 * the hole is (n+1)*4.
 */
export class HoleMesh extends MeshWriter {
    width: number;
    height: number;
    radius: number;
    n: number;

    constructor(width: number, height: number, radius: number, n: number) {
        super();
        this.n = n;
        this.width = width;
        this.height = height;
        this.radius = radius;
    }

    get attrs() {
        const result = new Set<Attr>();
        // Eight triangles at n=0, then four more for every n over 0. Here count
        // signifies the number of vertexes.
        const count = 24 + 12*this.n;
        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const n = this.n,
            nm1 = n - 1,
            width = this.width,
            height = this.height,
            radius = this.radius,
            ox = width/2,
            oy = height/2,
            inc = n === 0 ? Math.PI/2 : Math.PI / (2*(n+1)),
            [_p, pos] = attrMap.byName('position'),
            len=4+n*4;

        // Usually I would just accumulate inc into the t parameter on each
        // loop, i.e. `t+=inc`, but here that leads to an accumulated error
        // that pushes the final vertex past one full revolution.
        let a = [ox+radius, oy], j = 0, k = 0;
        for(let i=0; i < len; i++, j+=9, k+=6) {
            const t = (i+1)*inc,
                sin = Math.sin(t),
                cos = Math.cos(t),
                b = [cos < 0 ? 0 : width,  sin < 0 ? 0 : height],
                c = [ox+cos*radius, oy+sin*radius];
            pos.set([...a, 0, ...b, 0, ...c, 0], j);
            a = c;
        }

        // Now we manually fill in the last four triangles that connect the
        // edges of the quad to the top, bottom, left
        pos.set([
            // top
            0, height, 0,       ox, oy+radius, 0,   width, height, 0,
            // right
            width, height, 0,   ox+radius, oy, 0,   width, 0, 0,
            // bottom
            0, 0, 0,            width, 0, 0,        ox, oy-radius, 0,
            // left
            0, 0, 0,            ox-radius, oy, 0,   0, height, 0
        ], j);
    }
}


export class PolygridMesh extends MeshWriter {
    voids: [number, number, number, number][];
    nodes: [number, number][];
    join: Join;

    constructor() {
        super();
        this.voids = [];
        this.nodes = [];
    }

    rect(x: number, y: number, width: number, height: number) {
        this.nodes.push([x, y], [x+width, y+height]);
    }

    void(x: number, y: number, width: number, height: number) {
        this.nodes.push([x, y], [x+width, y+height]);
        this.voids.push([x, y, x+width, y+height]);
    }

    isInVoid(x1: number, y1: number, x2: number, y2: number) {
        for(let v of this.voids) {
            if(x1 >= v[0] && y1 >= v[1] && x2 <= v[2] && y2 <= v[3]) {
                return true;
            }
        }
        return false;
    }

    get attrs() {
        let xs: Array<number> | Set<number> = new Set(),
            ys: Array<number> | Set<number> = new Set();
        for(let [x, y] of this.nodes) {
            xs.add(x); ys.add(y);
        }

        xs = Array.from(xs);
        ys = Array.from(ys);
        const comp = (a: number, b: number) => a-b;
        xs.sort(comp); ys.sort(comp);
        const xcount = xs.length - 1,
              ycount = ys.length - 1;

        const planes = new Array();
        for(let i=0; i < xcount; i++) {
            for(let j=0; j < ycount; j++) {
                if(this.isInVoid(xs[i], ys[j], xs[i+1], ys[j+1])) {
                    continue;
                } else {
                    const x = xs[i],
                          y = ys[j],
                          width = xs[i+1] - x,
                          height = ys[j+1] - y;
                    planes.push(new PlaneMesh(x, y, width, height));
                }
            }
        }

        this.join = new Join(planes);
        return this.join.attrs;
    }

    write(attrMap: AttrMap) {
        this.join.write(attrMap);
    }

}

/**
 * The EdgeList class represents 2D data in the XY plane that can be
 * extruded around another EdgeList. It's very important that polygons used
 * as the path for extrusions have vertex normals at each vertex. Vertex
 * normals imputed by bisecting the angle to neighboring vertices will be
 * incorrect at many boundaries.
 *
 * EdgeLists built in the counter-clockwise direction will have their
 * exterior surface on the outside.
 *
 * We have some redundant storage when edges are connected and have smooth
 * normals, but having each edge store both endpoints with unique normals
 * means the edge list can have gaps
 */
export class EdgeList {
    verts: number[];
    closed: boolean;

    lastX: number = 0;
    lastY: number = 0;

    constructor() {
        this.verts = [];
        this.closed = false;
    }

    get edgeCount() {
        return this.verts.length/8;
    }
    
    /**
     * Yields triplets of edges as `{prev, edge, next}` where each edge is in
     * the form [x1, y1, nx1, ny1, x2, y2, nx2, ny2]. The `n` prefixed entries
     * are the normal vector (pure direction).
     *
     * If there is one edge, the iterator will yield one edge. If there are two
     * or more edges and the `closed` property is true, then you will get an 
     * additional edge that goes from the last to the first vertex.
     *
     * The `prev` and `next` edges may be null for the first and last edges
     * respectively. If the `closed` property is true, then the `prev` and
     * `next` edges will never be null and will "wrap around" the list of
     * vertices. In the case of a single edge with `closed = true`, this
     * doesn't make much sense, but it will happen without error.
     */
    *edges() {
        const v = this.verts;

        // If we don't even have one edge, there's nothing we can do
        if(v.length < 8) {
            return;
        }

        // Take care of the first edge as a special case.
        {
            const prev = this.closed ? v.slice(-8) : null;
            const edge = v.slice(0, 8);
            let next = null;
            if(v.length >= 16) {
                next = v.slice(8, 16);
            } else if(this.closed) {
                next = v.slice(-8);
            }

            yield { prev, edge, next };
        }

        // If we only had one edge, we're done, so we return early
        if(v.length == 8) {
            return;
        }

        // Loop over all the edge pairs in the middle of the array. This might
        // be none if there are two edges.
        const limit = this.edgeCount - 1;
        for(let i=1, j=8; i < limit; i++, j+=8) {
            const prev = v.slice(j-8, j);
            const edge = v.slice(j, j+8);
            const next = v.slice(j+8, j+16);

            yield { prev, edge, next };
        }

        // Do the last explicit edge as a special case.
        if(v.length >= 16) {
            const prev = v.slice(-16, -8);
            const edge = v.slice(-8);
            const next = this.closed ? v.slice(0, 8) : null;
            yield { prev, edge, next };
        }
    }

    push(ax: number, ay: number, anx: number, any: number,
         bx: number, by: number, bnx: number, bny: number)
    {
        this.verts.push(ax, ay, anx, any, bx, by, bnx, bny);
        this.lastX = bx;
        this.lastY = by;
    }

    /**
     * Pushes another point, using an automatic vertex normal that's
     * perpendicular to the path of travel.
     */
    move(dx: number, dy: number) {
        // nx, ny are a 90 degree clockwise rotation from the vector (dx, dy),
        // and they're normalized so that they'll give a good dot product later.
        const
            r = Math.sqrt(dx*dx + dy*dy),
            nx = dy/r,
            ny = -dx/r,
            v = this.verts,
            l = v.length,
            x = v[l-4] + dx,
            y = v[l-3] + dy;
        this.push(this.lastX, this.lastY, nx, ny, x, y, nx, ny);
        this.lastX = x;
        this.lastY = y;
    }

    /**
     * Automatically closes a path by moving to the first point. This might be
     * convenient in some cases. A path can be manually marked as closed by
     * setting the `closed` property, provided the last point is the same as the
     * first.
     */
    close() {
        const
            v = this.verts,
            l = v.length,
            x1 = v[0],      y1 = v[1],
            x2 = v[l-4],    y2 = v[l-3];

        this.move(x1-x2, y1-y2);

        this.closed = true;
    }

    /**
     * Starting from wherever the last point was drawn, draw an arc with n
     * segments. The minimum n is 1, lower values are ignored.
     */
    arc(start: number, theta: number, r: number, n: number) {
        n = Math.max(1, n);
        const
            x = this.lastX,
            y = this.lastY,
            cx = x - r*Math.cos(start),
            cy = y - r*Math.sin(start),
            inc = theta / n;

        for(let i=1; i <= n; i++) {
            const
                t = start + i*inc,
                cos = Math.cos(t),
                sin = Math.sin(t);
            // Push the point, plus a normal that extends along the radius
            this.push(this.lastX, this.lastY, cos, sin,
                      cx + (r*cos), cy + (r*sin), cos, sin);
        }
    }

    /**
     * This generates a BufferGeometry appropriate for use with three.js
     * LineSegments. Each output line segment either represents a segment of
     * the polygon, or a normal vector.
     */
    toBufferGeometry() {
        const
            geo = new BufferGeometry(),
            d = new Float32Array(6*this.edgeCount),
            pos = new BufferAttribute(d, 3);

        let i = 0;
        for(const edge of this.edges()) {
            const [x1, y1, _nx1, _ny1, x2, y2, _nx2, _ny2] = edge.edge;

            d[i] = x1;
            d[i+1] = y1;

            d[i+3] = x2;
            d[i+4] = y2;

            i+= 6;
        }

        geo.setAttribute('position', pos);
        return geo;
    }

    normalsToBufferGeometry() {
        const
            geo = new BufferGeometry(),
            d = new Float32Array(12*this.edgeCount),
            pos = new BufferAttribute(d, 3);

        let i = 0;
        for(const edge of this.edges()) {
            const [x1, y1, nx1, ny1, x2, y2, nx2, ny2] = edge.edge;

            d[i] = x1;
            d[i+1] = y1;

            d[i+3] = x1+nx1;
            d[i+4] = y1+ny1;

            d[i+6] = x2;
            d[i+7] = y2;

            d[i+9] = x2+nx2;
            d[i+10] = y2+ny2;

            i+= 12;
        }

        geo.setAttribute('position', pos);
        return geo;
    }

    /**
     * Convert this to a group of `THREE.LineSegments` that can be added
     * directly to the scene.
     */
    toLineSegments(color: number = 0x000000, normalColor: number = 0xFF00FF) {

        const
            group = new Group(),
            edges = this.toBufferGeometry(),
            normals = this.normalsToBufferGeometry();

        group.add(new LineSegments(edges, new LineBasicMaterial({color})));
        group.add(new LineSegments(
            normals, new LineBasicMaterial({color: normalColor})));
        return group;
    }

    /**
     * Examines every edge in a buffer geometry and extracts the vertex edge
     * position and normals for the edges that lay on the ground plane with
     * normals also in the ground plane. From a practical perspective, this
     * means creating a model of a profile in Blender that appears in elevation
     * (in the XZ plane), and is extruded towards the front (negative Y axis).
     * When this comes into three.js, it will be in the XY plane, which makes
     * it more convenient to go from XYZ to XY. See 'jarlin_crown.blend' for an
     * example.
     *
     * Note also, that modeling the profiles in this way is basically necessary
     * to get Blender's modeling tools to assign the normals that we want.
     * Modeling the profile as just edges without faces, or only as faces in a
     * single plane, would result in normals pointing out of the plane, whereas
     * we want normals that make sense for the extruded surface, AKA the swept
     * profile.
     *
     * Since we're using the GLB format for this, we assume that the input
     * is indexed.
     */
    fromBufferGeometry(geo: BufferGeometry) {
        if(geo.index == null) {
            throw new MeshWriterError('EdgeList.fromBufferGeometry only works with indexed geometries');
        }

        const
            p = geo.getAttribute('position')?.array,
            n = geo.getAttribute('normal')?.array,
            idx = geo.index.array;

        if(n == null) {
            throw new MeshWriterError('EdgeList.fromBufferGeometry requires normal data in the input geometry');
        }

        // threshold of being near zero
        const lt = 1e-5;
        const gt = -lt;

        // iterate over the index array in groups of three (triangles) and stop
        // when the last vertex is at the end of the index array
        for(let i = 0, j = 1, k = 2; k < geo.index.array.length; i+=3, j+=3, k+=3)
        {
            // we'll extract points a, b, and c from the array, along with
            // their corresponding normal vectors. We throw away the z
            // component, but we do use it to check if we're in the XY plane
            const
                // indices of each coordinate
                iax = idx[i]*3, iay = iax+1, iaz = iay+1,
                ibx = idx[j]*3, iby = ibx+1, ibz = iby+1,
                icx = idx[k]*3, icy = icx+1, icz = icy+1,
   
                // position of each vertex
                pax = p[iax], pay = p[iay], paz = p[iaz],
                pbx = p[ibx], pby = p[iby], pbz = p[ibz],
                pcx = p[icx], pcy = p[icy], pcz = p[icz],

                // normal of each vertex
                nax = n[iax], nay = n[iay], naz = n[iaz],
                nbx = n[ibx], nby = n[iby], nbz = n[ibz],
                ncx = n[icx], ncy = n[icy], ncz = n[icz],
                
                // a, b, c are booleans testing if the vertex and its normals
                // all lay in the XY plane, i.e. z = 0;
                a = paz > gt && paz < lt && naz > gt && naz < lt,
                b = pbz > gt && pbz < lt && nbz > gt && nbz < lt,
                c = pcz > gt && pcz < lt && ncz > gt && ncz < lt;
            
            if(a && b) {
                this.push(pax, pay, nax, nay, pbx, pby, nbx, nby);
            }
            if (b && c) {
                this.push(pbx, pby, nbx, nby, pcx, pcy, ncx, ncy);
            }
            if (c && a) {
                this.push(pcx, pcy, ncx, ncy, pax, pay, nax, nay);
            }
        }
        this.sort();
    }

    /**
     * This is a subroutine of fromBufferGeometry that puts the edges in order
     * based on the assumption that the edge list is closed so we can start
     * from any random edge and get a good result. This also assumes that the
     * edges are all connected. Breaks in the profile will produce bad results.
     */
    sort() {
        // we never have to fix the last edge because it's guaranteed to be
        // sorted correctly when we check the second-to-last edge.
        const limit = this.verts.length - 8;
        for(let i = 0; i < limit; i+= 8) {
            // if we label the two endpoints of the edge a and b, then b's x
            // and y coordinates are the fourth and fifth elements respectively
            const
                bx = this.verts[i+4],
                by = this.verts[i+5];

            {
                // continuing to label endpoints c and d as the endpoints of
                // the edge following ab.
                const
                    cx = this.verts[i+8],
                    cy = this.verts[i+9];
                // if the next edge is already connected to this one, then we
                // have no work to do in this loop iteration.
                if(bx == cx && by == cy) {
                    continue;
                }
            }

            // We're searching for the edge that connects with this one. We
            // already checked the one right after position `i`, so now we
            // start with the one after that and go all the way through the
            // end of the array.
            for(let j = i+16; j < this.verts.length; j += 8) {
                const
                    cx = this.verts[j],
                    cy = this.verts[j+1];

                if(bx == cx && by == cy) {
                    // We found the connection! The edge at i connects to the
                    // edge at j. So we want to swap the edge after i (at i+8)
                    // with the edge at j. Like any swap, we copy the first
                    // destination into a teporary variable, `edge`.
                    const edge = this.verts.slice(i+8, i+16);

                    // Then we overwrite the area previously occupied by edge
                    // with the data at j.
                    this.verts.copyWithin(i+8, j, j+8);

                    // Finally, we copy the temporary variable back into the
                    // array at position j
                    for(let k=0; k < 8; k++) {
                        this.verts[j+k] = edge[k];
                    }
                    break;
                }
            }
        }
    }
}


/**
 * This is a bit different to the DiagridFaceMesh, which only provides a
 * repeatable subunit of the face grid. Here we provide the entire face grid
 * in one go.
 */
export class GridFaceMesh extends MeshWriter {
    width: number;
    height: number;
    thickness: number;
    countX: number;
    countY: number;

    constructor(width: number, height: number, thickness: number,
                countX: number, countY: number)
    {
        super();
        this.width = width;
        this.height = height;
        this.thickness = thickness;
        this.countX = countX;
        this.countY = countY;
    }

    get attrs() {
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3. Our horizontal quads run
        // the full span, uninterrupted. Our vertical quads are interrupted by
        // each of the horizontals, so they're multiplied by the number of
        // horizontal spans.

        const
            result = new Set<Attr>(),
            cx = this.countX,
            cy = this.countY,
            h = cy - 1,          // the count of horizontal quads
            v = (cx - 1) * cy,   // the count of vertical quads
            count = 6 * (h + v); // two tris and three verts (6) per quad

        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width,
            h = this.height,
            t = this.thickness,
            t2 = t/2,
            cx = this.countX,
            cy = this.countY,
            hinc = (w - t*(cx - 1)) / cx,  // distance between horizals
            vinc = (h - t*(cy - 1)) / cy,  // distance between verticals
            ut = t/w,  // the thickness as a portion of the width
            vt = vinc/w,
            [_p, pos] = attrMap.byName('position'),
            [_u, uv] = attrMap.byName('uv');

        // Write the horizontal quads and the vertical quads above them
        let j = 0, k = 0;
        for(let i = 1; i < cy; i++) {
            const
                y0 = i*vinc + t*(i-1),
                y1 = y0 + t,
                y2 = y1 + vinc, // the top of the vertical quads for this row
                u0 = i * ut,
                u1 = u0 + ut;
            j = PlaneMesh.write3(pos, j,
                0, y0, 0,  // lower left
                w, y0, 0,  // lower right
                w, y1, 0,  // upper right
                0, y1, 0); // upper left
            k = PlaneMesh.write2(uv, k,
                u1, 0,  // lower left
                u1, 1,  // lower right
                u0, 1,  // upper right
                u0, 0); // upper left

            // write the vertical quads
            for(let m=1; m < cx; m++) {
                const
                    x0 = m*hinc + t*(m-1),
                    x1 = x0 + t,
                    v0 = i * vt,
                    v1 = v0 + vt;
                j = PlaneMesh.write3(pos, j,
                    x0, y1, 0,  // lower left
                    x1, y1, 0,  // lower right
                    x1, y2, 0,  // upper right
                    x0, y2, 0); // upper left
                k = PlaneMesh.write2(uv, k,
                    0,  v0,  // lower left
                    ut, v0,  // lower right
                    ut, v1,  // upper right
                    0,  v1); // upper left
            }
        }
        // Write the last row of vertical quads (right at the bottom)
        // write the vertical quads
        const y1 = 0, y2 = vinc;
        for(let m=1; m < cx; m++) {
            const
                x0 = m*hinc + t*(m-1),
                x1 = x0 + t,
                v0 = 0,
                v1 = vt;
            j = PlaneMesh.write3(pos, j,
                x0, y1, 0,  // lower left
                x1, y1, 0,  // lower right
                x1, y2, 0,  // upper right
                x0, y2, 0); // upper left
            k = PlaneMesh.write2(uv, k,
                0,  v0,  // lower left
                ut, v0,  // lower right
                ut, v1,  // upper right
                0,  v1); // upper left
        }
    }
}


/**
 * This matches the GridFaceMesh in that it produces all the boxes in one go.
 */
export class GridBoxMesh extends MeshWriter {
    width: number;
    height: number;
    depth: number;
    thickness: number;
    countX: number;
    countY: number;

    constructor(width: number, height: number, depth: number, thickness: number,
                countX: number, countY: number)
    {
        super();
        this.width = width;
        this.height = height;
        this.depth = depth;
        this.thickness = thickness;
        this.countX = countX;
        this.countY = countY;
    }

    get attrs() {
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3. Our horizontal quads run
        // the full span, uninterrupted. Our vertical quads are interrupted by
        // each of the horizontals, so they're multiplied by the number of
        // horizontal spans.

        const
            result = new Set<Attr>(),
            cx = this.countX,
            cy = this.countY,
            h = 2*(cy - 1),      // the count of horizontal quads
            v = cy*2*(cx - 1),   // the count of vertical quads
            count = 6 * (h + v); // two tris and three verts (6) per quad

        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        result.add(new Attr('normal', 3, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width,
            h = this.height,
            d = this.depth,
            t = this.thickness,
            t2 = t/2,
            cx = this.countX,
            cy = this.countY,
            hinc = (w - t*(cx - 1)) / cx,  // distance between horizals
            vinc = (h - t*(cy - 1)) / cy,  // distance between verticals
            ut = t/w,  // the thickness as a portion of the width
            vt = vinc/w,
            [_p, pos] = attrMap.byName('position'),
            [_u, uv] = attrMap.byName('uv'),
            [_n, normal] = attrMap.byName('normal');

        // Write the horizontal quads and the vertical quads above them
        let j = 0, k = 0, n = 0;
        for(let i = 1; i < cy; i++) {
            const
                y0 = i*vinc + t*(i-1),
                y1 = y0 + t,
                y2 = y1 + vinc, // the top of the vertical quads for this row
                v0 = 0,
                v1 = d/w;
            // Lower horizontal quad
            j = PlaneMesh.write3(pos, j,
                0, y0, 0,  // back, lower left
                w, y0, 0,  // back, lower right
                w, y0, d,  // front, lower right
                0, y0, d,  // front, lower left
            );
            k = PlaneMesh.write2(uv, k,
                0, v0,  // lower left
                1, v0,  // lower right
                1, v1,  // upper right
                0, v1); // upper left
            n = PlaneMesh.write3(normal, n,
                0, -1, 0,  0, -1, 0,  0, -1, 0,  0, -1, 0 );

            // Upper horizontal quad
            j = PlaneMesh.write3(pos, j,
                w, y1, 0,  // back, upper right
                0, y1, 0,  // back, upper left
                0, y1, d,  // front, upper left
                w, y1, d,  // front, upper right
            );
            k = PlaneMesh.write2(uv, k,
                1, v0,  // lower left
                0, v0,  // lower right
                0, v1,  // upper right
                1, v1); // upper left
            n = PlaneMesh.write3(normal, n,
                    0, 1, 0,  0, 1, 0,  0, 1, 0,  0, 1, 0);


            // write the vertical quads
            for(let m=1; m < cx; m++) {
                const
                    x0 = m*hinc + t*(m-1),
                    x1 = x0 + t,
                    v0 = i * vt,
                    v1 = v0 + vt;
                j = PlaneMesh.write3(pos, j,
                    x0, y1, d,  // front lower left
                    x0, y2, d,  // front upper left
                    x0, y2, 0,  // back upper left
                    x0, y1, 0,  // back lower right
                );
                k = PlaneMesh.write2(uv, k,
                    0, v0,  // lower left
                    0, v1,  // upper left
                    1, v1,
                    1, v0,
                );
                n = PlaneMesh.write3(normal, n,
                    -1, 0, 0,  -1, 0, 0,  -1, 0, 0,  -1, 0, 0);

                j = PlaneMesh.write3(pos, j,
                    x1, y1, 0,  // back lower right
                    x1, y2, 0,  // back upper right
                    x1, y2, d,  // front upper right
                    x1, y1, d,  // front lower right
                );
                k = PlaneMesh.write2(uv, k,
                    0, v0,  // lower right
                    0, v1,  // upper right
                    1, v1,
                    1, v0,
                );
                n = PlaneMesh.write3(normal, n,
                    1, 0, 0,  1, 0, 0,  1, 0, 0,  1, 0, 0);
            }
        }
        // write the bottom row of vertical quads
        const y1 = 0, y2 = vinc;
        for(let m=1; m < cx; m++) {
            const
                x0 = m*hinc + t*(m-1),
                x1 = x0 + t,
                v0 = vt,
                v1 = v0 + vt;
            j = PlaneMesh.write3(pos, j,
                x0, y1, d,  // front lower left
                x0, y2, d,  // front upper left
                x0, y2, 0,  // back upper left
                x0, y1, 0,  // back lower right
            );
            k = PlaneMesh.write2(uv, k,
                0, v0,  // lower left
                0, v1,  // upper left
                1, v1,
                1, v0,
            );
            n = PlaneMesh.write3(normal, n,
                -1, 0, 0,  -1, 0, 0,  -1, 0, 0,  -1, 0, 0);

            j = PlaneMesh.write3(pos, j,
                x1, y1, 0,  // back lower right
                x1, y2, 0,  // back upper right
                x1, y2, d,  // front upper right
                x1, y1, d,  // front lower right
            );
            k = PlaneMesh.write2(uv, k,
                0, v0,  // lower right
                0, v1,  // upper right
                1, v1,
                1, v0,
            );
            n = PlaneMesh.write3(normal, n,
                1, 0, 0,  1, 0, 0,  1, 0, 0,  1, 0, 0);
        }

    }
}

/**
 * This is the front edge banding of a diagonal cubby.
 */
export class DiagridFaceMesh extends MeshWriter {
    width: number;
    height: number;
    thickness: number;

    constructor(width: number, height: number, thickness: number) {
        super();
        this.width = width;
        this.height = height;
        this.thickness = thickness;
    }

    get attrs() {
        const result = new Set<Attr>();
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3.
        const count = 48;  // 3 verts times 8 quads, times 2 tris per quad
        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width,
            h = this.height,
            t = this.thickness,
            vp = DiagridFaceMesh.diagridPos(w, h, t),
            vu = DiagridFaceMesh.diagridUV(w, h, t),
            [_p, pos] = attrMap.byName('position'),
            [_u, uv] = attrMap.byName('uv');

        let i = 0;
        // Quad 1, 10, 8, 2
        i = PlaneMesh.write3(pos, i,
            vp[1][0],  vp[1][1],  0,
            vp[10][0], vp[10][1], 0,
            vp[8][0],  vp[8][1],  0,
            vp[2][0],  vp[2][1],  0);
        // Quad 1, 5, 12, 10
        i = PlaneMesh.write3(pos, i,
            vp[1][0],  vp[1][1],  0,
            vp[5][0],  vp[5][1],  0,
            vp[12][0], vp[12][1], 0,
            vp[10][0], vp[10][1], 0);
        // Quad 4, 3, 7, 8
        i = PlaneMesh.write3(pos, i,
            vp[4][0],  vp[4][1], 0,
            vp[3][0],  vp[3][1], 0,
            vp[7][0],  vp[7][1], 0,
            vp[8][0],  vp[8][1], 0);
        // Quad 4, 8, 11, 6
        i = PlaneMesh.write3(pos, i,
            vp[4][0],  vp[4][1],  0,
            vp[8][0],  vp[8][1],  0,
            vp[11][0], vp[11][1], 0,
            vp[6][0],  vp[6][1],  0);
        // Quad 16, 12, 9, 14
        i = PlaneMesh.write3(pos, i,
            vp[16][0], vp[16][1], 0,
            vp[12][0], vp[12][1], 0,
            vp[9][0],  vp[9][1],  0,
            vp[14][0], vp[14][1], 0);
        // Quad 16, 17, 13, 12
        i = PlaneMesh.write3(pos, i,
            vp[16][0], vp[16][1], 0,
            vp[17][0], vp[17][1], 0,
            vp[13][0], vp[13][1], 0,
            vp[12][0], vp[12][1], 0);
        // Quad 19, 10, 12, 18
        i = PlaneMesh.write3(pos, i,
            vp[19][0], vp[19][1], 0,
            vp[10][0], vp[10][1], 0,
            vp[12][0], vp[12][1], 0,
            vp[18][0], vp[18][1], 0);
        // Quad 19, 15, 8, 10
        i = PlaneMesh.write3(pos, i,
            vp[19][0], vp[19][1], 0,
            vp[15][0], vp[15][1], 0,
            vp[8][0],  vp[8][1],  0,
            vp[10][0], vp[10][1], 0);

        // Write the UVs. Elsewhere this u is labeled t, but we'll use u here to
        // avoid conflicting with the alias for the thickness parameter. This u
        // is the scaled version of t for the UV coordinates.
        const
            g = Math.sqrt(w*w + h*h),
            u = t*(1/g),
            u2 = u/2;

        i = 0;
        // Quad 1: 1, 10, 8, 2
        i = PlaneMesh.write2(uv, i,
            vu[1][0],  vu[1][1],
            vu[10][0], vu[10][1],
            0,         vu[8][1],
            vu[2][0],  vu[2][1]);
        // Quad 2: 1, 5, 12, 10
        i = PlaneMesh.write2(uv, i,
            vu[1][0],  vu[1][1],
            vu[5][0],  vu[5][1],
            u,         vu[12][1],
            vu[10][0], vu[10][1]);
        // Quad 3: 4, 3, 7, 8
        i = PlaneMesh.write2(uv, i,
            vu[4][0],  vu[4][1],
            vu[3][0],  vu[3][1],
            vu[7][0],  vu[7][1],
            u2,        vu[8][1]);
        // Quad 4: 4, 8, 11, 6
        i = PlaneMesh.write2(uv, i,
            vu[4][0],  vu[4][1],
            u2,        vu[8][1],
            vu[11][0], vu[11][1],
            vu[6][0],  vu[6][1]);
        // Quad 5: 16, 12, 9, 14
        i = PlaneMesh.write2(uv, i,
            vu[16][0], vu[16][1],
            u2,        vu[12][1],
            vu[9][0],  vu[9][1],
            vu[14][0], vu[14][1]);
        // Quad 6: 16, 17, 13, 12
        i = PlaneMesh.write2(uv, i,
            vu[16][0], vu[16][1],
            vu[17][0], vu[17][1],
            vu[13][0], vu[13][1],
            u2,        vu[12][1]);
        // Quad 7: 19, 10, 12, 18
        i = PlaneMesh.write2(uv, i,
            vu[19][0], vu[19][1],
            vu[10][0], vu[10][1],
            u,         vu[12][1],
            vu[18][0], vu[18][1]);
        // Quad 8: 19, 15, 8, 10
        i = PlaneMesh.write2(uv, i,
            vu[19][0], vu[19][1],
            vu[15][0], vu[15][1],
            0,         vu[8][1],
            vu[10][0], vu[10][1]);

    }

    /**
     * Generate the X, Y positions of the diagrid face vertexes.
     */
    static diagridPos(w: number, h: number, t: number) {
        const
            a = Math.atan2(w, h),       // angle alpha
            x = t / (2*Math.sin(Math.PI/2 - a)),
            y = t / (2*Math.sin(a)),
            w2 = w/2,
            h2 = h/2,
            t2 = t/2,
            x2 = x/2,
            y2 = y/2,
            hmy = h-y,
            wmx = w-x;
        return [
            [], // Include an extra so we can use 1-based indexes
            [0,     h],
            [x,     h],
            [wmx,   h],
            [w,     h],
            [0,     hmy],
            [w,     hmy],
            [w2,    h2+y],
            [w2+x2, h2+y2],
            [w2-x,  h2],
            [w2,    h2],
            [w2+x,  h2],
            [w2-x2, h2-y2],
            [w2,    h2-y],
            [0,     y],
            [w,     y],
            [0,     0],
            [x,     0],
            [wmx,   0],
            [w,     0]
        ];
    }

    static diagridUV(w: number, h: number, tw: number) {
        const
            g = Math.sqrt(w*w + h*h),
            t = tw*(1/g),
            t2 = t/2;
        return [
            [], // Include an extra so we can use 1-based indexes
            [t2,    0],
            [0,     0],
            [0,     1],
            [t2,    1],
            [t,     0],
            [t,     1],
            [0,     0.5],
            [null,  0.5],
            [0,     0.5],
            [t2,    0.5],
            [t,     0.5],
            [null,  0.5],
            [t,     0.5],
            [0,     0],
            [0,     1],
            [t2,    0],
            [t,     0],
            [t,     1],
            [t2,    1]
        ];
    }
}

/**
 * These are the backing shelves of the diagonal cubby.
 */
export class DiagridBoxMesh extends MeshWriter {
    width: number;
    height: number;
    depth: number;
    thickness: number;

    constructor(width: number, height: number, depth: number, thickness: number) {
        super()
        this.width = width;
        this.height = height;
        this.depth = depth;
        this.thickness = thickness;
    }

    get attrs() {
        const result = new Set<Attr>();
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3.
        const count = 48;  // 3 verts times 8 quads, times 2 tris per quad
        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('normal', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            w = this.width,
            h = this.height,
            d = this.depth,
            t = this.thickness,
            a = Math.atan2(w, h),       // angle alpha
            cosa = Math.cos(a),
            sina = Math.sin(a),
            vp = DiagridFaceMesh.diagridPos(w, h, t),
            [_p, pos] = attrMap.byName('position'),
            [_n, n] = attrMap.byName('normal'),
            [_u, uv] = attrMap.byName('uv');

        const edges = [
            [7,     2,  cosa,   sina],
            [3,     7,  -cosa,  sina],
            [5,     9,  -cosa,  -sina],
            [9,     14, -cosa,  sina],
            [17,    13, cosa,   -sina],
            [13,    18, -cosa,  -sina],
            [15,    11, cosa,   sina],
            [11,    6,  cosa,   -sina]];

        let i = 0, j = 0;
        for(let [a, b, nx, ny] of edges) {
            PlaneMesh.write3(pos, i,
                vp[a][0],  vp[a][1],  0,
                vp[b][0],  vp[b][1],  0,
                vp[b][0],  vp[b][1],  d,
                vp[a][0],  vp[a][1],  d);
            i = PlaneMesh.write3(n, i,
                nx,  ny,  0,
                nx,  ny,  0,
                nx,  ny,  0,
                nx,  ny,  0);
            j = PlaneMesh.write2(uv, j, 0, 0, 1, 0, 1, 1, 0, 1);
        }
    }
}


export class FuncMesh extends MeshWriter {
    uStart: number;
    uEnd: number;
    uCount: number;
    vStart: number;
    vEnd: number;
    vCount: number;
    xfn: (u: number, v: number) => number;
    yfn: (u: number, v: number) => number;
    zfn: (u: number, v: number) => number;

    constructor(uStart: number, uEnd: number, uCount: number,
                vStart: number, vEnd: number, vCount: number,
                xfn: (u: number, v: number) => number,
                yfn: (u: number, v: number) => number,
                zfn: (u: number, v: number) => number)
    {
        super();
        this.uStart = uStart;
        this.uEnd = uEnd;
        this.uCount = uCount;
        this.vStart = vStart;
        this.vEnd = vEnd;
        this.vCount = vCount;
        this.xfn = xfn;
        this.yfn = yfn;
        this.zfn = zfn;
    }

    get attrs() {
        const result = new Set<Attr>();
        // Count is how many vertexes we have... since we're working with
        // triangles, this is always a multiple of 3.
        const count = 6 * this.uCount * this.vCount;
        // name, size, count, type
        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('normal', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));
        return result;
    }

    write(attrMap: AttrMap) {
        const
            lerp = (a: number, b: number, t: number) => (1-t)*a + t*b,
            uStart = this.uStart,
            uEnd = this.uEnd,
            uCount = this.uCount,
            ucm1 = uCount - 1,
            vStart = this.vStart,
            vEnd = this.vEnd,
            vCount = this.vCount,
            vcm1 = vCount - 1,
            vcp1 = vCount + 1,
            xfn = this.xfn,
            yfn = this.yfn,
            zfn = this.zfn,
            [_p, pos] = attrMap.byName('position'),
            [_n, normal] = attrMap.byName('normal'),
            [_u, uv] = attrMap.byName('uv'),
            uvs = Array(uCount),
            verts = Array(uCount),
            ns = Array(uCount),

            // Some ad hoc vector operations
            minus = (v1: number[], v2: number[]) => [v1[0]-v2[0], v1[1]-v2[1], v1[2]-v2[2]],
            normalize = (v: number[]) => {
                const r = 1 / Math.sqrt(v[0]*v[0]+v[1]*v[1]+v[2]*v[2]);
                return [r*v[0], r*v[1], r*v[2]];
            },
            cross = (v1: number[], v2: number[]) => [
                v1[1]*v2[2] - v1[2]*v2[1],
                v1[2]*v2[0] - v1[0]*v2[2],
                v1[0]*v2[1] - v1[1]*v2[0],
            ];


        // Calculate the UV coordinates, XYZ positions, and normals for each
        // vertex. Note that we're sampling the x, y, and z functions at +/-
        // 0.5 u and v to determine the normal. This is more straightforward
        // than averaging neighboring vertexes.
        for(let ui=0; ui < uCount; ui++) {
            const
                u = lerp(uStart, uEnd, ui/ucm1),
                up = lerp(uStart, uEnd, (ui+0.5)/ucm1),
                um = lerp(uStart, uEnd, (ui-0.5)/ucm1);
            uvs[ui] = Array(vcp1);
            verts[ui] = Array(vcp1);
            ns[ui] = Array(vcp1)
            for(let vi=0; vi < vCount; vi++) {
                const
                    v = lerp(vStart, vEnd, vi/vcm1),
                    vp = lerp(vStart, vEnd, (vi+0.5)/vcm1),
                    vm = lerp(vStart, vEnd, (vi-0.5)/vcm1),
                    nup = [xfn(up, v), yfn(up, v), zfn(up, v)],
                    num = [xfn(um, v), yfn(um, v), zfn(um, v)],
                    nvp = [xfn(u, vp), yfn(u, vp), zfn(u, vp)],
                    nvm = [xfn(u, vm), yfn(u, vm), zfn(u, vm)];

                uvs[ui][vi] = [ui/ucm1, vi/vcm1];
                verts[ui][vi] = [xfn(u, v), yfn(u, v), zfn(u, v)];
                ns[ui][vi] = normalize(cross(
                    minus(nup, num),
                    minus(nvp, nvm),
                ));
            }
        }

        // Finally, we use the UV, XYZ position, and normals grid to write all
        // the output triangles. This is roughly equivalent to converting an
        // indexed quad mesh to a non-indexed triangle mesh.
        let i = 0, j = 0;
        for(let ui=1, uim1=0; ui < uCount; ui++, uim1++) {
            for(let vi=1, vim1=0; vi < vCount; vi++, vim1++) {
                PlaneMesh.write3(pos, i,
                    verts[uim1][vim1][0],
                    verts[uim1][vim1][1],
                    verts[uim1][vim1][2],

                    verts[ui][vim1][0],
                    verts[ui][vim1][1],
                    verts[ui][vim1][2],

                    verts[ui][vi][0],
                    verts[ui][vi][1],
                    verts[ui][vi][2],

                    verts[uim1][vi][0],
                    verts[uim1][vi][1],
                    verts[uim1][vi][2],
                );

                i = PlaneMesh.write3(normal, i,
                    ns[uim1][vim1][0],
                    ns[uim1][vim1][1],
                    ns[uim1][vim1][2],

                    ns[ui][vim1][0],
                    ns[ui][vim1][1],
                    ns[ui][vim1][2],

                    ns[ui][vi][0],
                    ns[ui][vi][1],
                    ns[ui][vi][2],

                    ns[uim1][vi][0],
                    ns[uim1][vi][1],
                    ns[uim1][vi][2],
                );

                j = PlaneMesh.write2(uv, j,
                    uvs[uim1][vim1][0],
                    uvs[uim1][vim1][1],

                    uvs[ui][vim1][0],
                    uvs[ui][vim1][1],

                    uvs[ui][vi][0],
                    uvs[ui][vi][1],

                    uvs[uim1][vi][0],
                    uvs[uim1][vi][1],
                );
            }
        }

    }
}

/**
 * A little baby Matrix4 transformation class to support the SweptMesh.
 */
export class Transform {
    sm: Matrix4; // scratch matrix
    sv: Vector3; // scratch vector;
    m: Matrix4; // the internal state of the transform

    constructor() {
        this.sm = new Matrix4();
        this.sv = new Vector3();
        this.m = new Matrix4();
    }

    apply(v: Vector3) {
        v.applyMatrix4(this.m);
    }

    translate(x: number, y: number, z: number) {
        this.sv.set(x, y, z);
        this.sm.makeTranslation(this.sv);
        this.m.multiply(this.sm);
    }

    rotateX(theta: number) {
        this.sm.makeRotationX(theta);
        this.m.multiply(this.sm);
    }

    rotateY(theta: number) {
        this.sm.makeRotationY(theta);
        this.m.multiply(this.sm);
    }

    rotateZ(theta: number) {
        this.sm.makeRotationZ(theta);
        this.m.multiply(this.sm);
    }

    shearXY(theta: number) {
        const slope = Math.tan(-theta);
        this.sm.makeShear(slope, 0, 0, 0, 0, 0);
        this.m.multiply(this.sm);
    }

    shearXZ(theta: number) {
        const slope = Math.tan(-theta);
        this.sm.makeShear(0, slope, 0, 0, 0, 0);
        this.m.multiply(this.sm);
    }

    /**
     * If theta is zero, the shear is a noop. If you want a shear to the left,
     * supply a positive angle measured from the vertical. If you want a shear
     * to the right, supply a negative angle, also measured from the vertical.
     * Values of theta outside the range [-pi/2, pi/2] may not work as you
     * expect and values of exactly positive or negative pi/2 will result in
     * an infinity.
     */
    shearYX(theta: number) {
        const slope = Math.tan(-theta);
        this.sm.makeShear(0, 0, slope, 0, 0, 0);
        this.m.multiply(this.sm);
    }

    shearZX(theta: number) {
        const slope = Math.tan(-theta);
        this.sm.makeShear(0, 0, 0, 0, slope, 0);
        this.m.multiply(this.sm);
    }

    shearZY(theta: number) {
        const slope = Math.tan(-theta);
        this.sm.makeShear(0, 0, 0, 0, 0, slope);
        this.m.multiply(this.sm);
    }

    reset() {
        this.m.identity();
    }
}

/**
 * The SweptMesh moves a 2D polygonal polygon along a 2D plan. Both the profile
 * and the plan are represented by the EdgeList class. The really important
 * reason this exists is that the resulting mesh includes normals and UVs
 * derived from the underlying polygons.
 *
 * Think of the plan object as blueprint drawing of the basic shape of the
 * resulting mesh in the ground plane. The profile object establishes the
 * elevation of the object. As a result, the (x, y) coordinates of the plan
 * object will lay in the XZ plane of the output object. The X axis of the
 * profile object will point in a direction perpendicular to the current edge of
 * the plan. The Y axis of the profile object will become the Y axis of the
 * resulting 3D object.
 *
 * The vertex normal of the resulting mesh points in the direction of the
 * profile object's vertex normal, rotated by the vertex normal of the plan
 * object.
 *
 * The U coordinate of each vertex is given by the vertex's distance around the
 * perimeter of the profile object. Similarly, the V coordinate is given by the
 * vertex's distance around the perimeter of the plan object.
 *
 * If the `smooth` constructor parameter is false (the default), then normals
 * are transformed orthogonal to the plan edge. If `smooth` is true, then
 * normals are transformed to the bisecting angle between edges.
 */
export class SweptMesh extends MeshWriter {
    profile: EdgeList;
    plan: EdgeList;
    smooth: boolean;

    constructor(profile: EdgeList, plan: EdgeList, smooth: boolean = false) {
        super();
        this.profile = profile;
        this.plan = plan;
        this.smooth = smooth;
    }

    get attrs() {
        const
            result = new Set<Attr>(),
            // The number of vertexes we'll need is two triangles per quad (6
            // vertexes) times a quad for every edge in the profile swept along
            // an edge in the plan.
            count = 6 * this.profile.edgeCount * this.plan.edgeCount;

        result.add(new Attr('position', 3, count, Float32Array));
        result.add(new Attr('normal', 3, count, Float32Array));
        result.add(new Attr('uv', 2, count, Float32Array));

        return result;
    }

    write(attrMap: AttrMap) {
        // For each edge in the plan:
        // - With a clean transformation matrix, move to the position of the
        //   first vertex, and rotate to the angle that bisects the adjacent
        //   edges.
        // - With another transformation matrix, move to the position of the
        //   second vertex and rotate to the corresponding bisecting angle
        // - Form a quad spanning each edge of the profile transformed with
        //   both of these matricies
        // - The normal of each vertex is the normal of the profile transformed
        // - The U of each vertex is its distance around the perimeter of the
        //   profile
        // - The V of each vertex is its position projected onto the plan edge
        const
            [_p, pos] = attrMap.byName('position'),
            [_n, normal] = attrMap.byName('normal'),
            [_u, uv] = attrMap.byName('uv'),

            // a, b, c, d should be a counterclockwise winding of a quad
            a = new Vector3(),
            b = new Vector3(),
            c = new Vector3(),
            d = new Vector3(),
            m1 = new Matrix4(),
            m2 = new Matrix4(),
            t1 = new Transform(),
            t2 = new Transform();

        // Indices for position, normal, and uv respectively
        let i = 0; let j = 0; let k = 0;

        // We want to treat the plan line segments as being in the XZ plane.
        for(const plan of this.plan.edges()) {

            // Compute the bisector angles of the current edge
            // etheta is the global measure of the edge angle
            const
                [ex1, ey1, _enx1, _eny1, ex2, ey2, _enx2, _eny2] = plan.edge,
                etheta = Math.atan2(ey2-ey1, ex2-ex1),
                planEdgeLen = Math.sqrt((ex2-ex1)**2 + (ey2-ey1)**2);


            // b1 is the global measure of the bisector between the edge and 
            // the previous edge
            let b1: number, b1g: number;
            if(plan.prev) {
                const [px1, py1, _pnx1, _pny1, px2, py2, pnx2, pny2] = plan.prev;
                // ptheta is the global angle of the previous edge
                const ptheta = Math.atan2(py2-py1, px2-px1);
                const normal = Math.atan2(pny2, pnx2);
                b1 = (etheta - ptheta) / 2;
                b1g = ptheta - normal;
            } else {
                b1 = 0;
                b1g = -etheta + Math.PI/2;
            }

            // b2 is the global measure of the bisector between the edge and
            // the next edge
            let b2: number, b2g: number;
            if(plan.next) {
                const [nx1, ny1, nnx1, nny1, nx2, ny2, _nnx2, _nny2] = plan.next;
                // ntheta is the global angle of the next edge.
                const ntheta = Math.atan2(ny2-ny1, nx2-nx1);
                const normal = Math.atan2(nny1, nnx1);
                b2 = (ntheta - etheta) / 2;
                b2g = etheta - normal;
            } else {
                b2 = 0;
                b2g = -etheta + Math.PI/2;
            }

            // We want to modify the Z coordinate by its X value, and the
            // coefficient is the tangent of the bisector angle.
            m1.makeShear(0, Math.tan(-b1), 0, 0, 0, 0);
            m2.makeShear(0, Math.tan(b2), 0, 0, 0, 0);

            // A running tally of the length of the profile perimeter
            let perimeter = 0;

            for(const profile of this.profile.edges()) {
                const [x1, y1, nx1, ny1, x2, y2, nx2, ny2] = profile.edge;

                const profileEdgeLen = Math.sqrt((x2-x1)**2 + (y2-y1)**2);

                // Calculate the positions. We shear the XY position of the
                // profile vertex to the desired angle, and then offset the
                // vertex position relative to the XZ position on the plan.
                t1.translate(ex1, 0, ey1);
                t1.rotateY(-etheta + Math.PI/2);

                t2.translate(ex2, 0, ey2);
                t2.rotateY(-etheta + Math.PI/2);
                
                // The oder here sets our winding order. We want edge ab to be
                // along the profile, edge bc along the plan, then edge cd in
                // the opposite direction along the profile, and edge da
                // backtracking to where we started at a.
                a.set(x1, y1, 0); a.applyMatrix4(m1); t1.apply(a);
                b.set(x2, y2, 0); b.applyMatrix4(m1); t1.apply(b);
                c.set(x2, y2, 0); c.applyMatrix4(m2); t2.apply(c);
                d.set(x1, y1, 0); d.applyMatrix4(m2); t2.apply(d);

                i = PlaneMesh.write3(pos, i,
                    a.x, a.y, a.z,
                    b.x, b.y, b.z,
                    c.x, c.y, c.z,
                    d.x, d.y, d.z);

                // Calculate the transformed surface normals. Because we know
                // this procedure does not scale the normal vector, we can use
                // plain matrix multiplication rather than the inverse
                // transpose used for general purpose transformations.
                if(this.smooth) {
                    t1.reset(); t2.reset();
// TODO: needs implementation, but not for crown molding. I suspect this would
// be easier to implement after a cleanup. I think a big reason I was getting
// in trouble is I was thinking of the normal vector of the plan as being
// relative to the plan edge. When generated by the EdgeList class, it's in
// object space, i.e. the global coordinate system.
//                    t1.translate(ex1, 0, ey1); t1.rotateY(b1g);
//                    t2.translate(ex2, 0, ey2); t2.rotateY(b2g);
                } else {
                    t1.reset(); t2.reset();
                    t1.rotateY(-etheta + Math.PI/2);
                    t2.rotateY(-etheta + Math.PI/2);
                }
               
                a.set(nx1, ny1, 0); t1.apply(a);
                b.set(nx2, ny2, 0); t1.apply(b);
                c.set(nx2, ny2, 0); t2.apply(c);
                d.set(nx1, ny1, 0); t2.apply(d);

                j = PlaneMesh.write3(normal, j,
                    a.x, a.y, a.z,
                    b.x, b.y, b.z,
                    c.x, c.y, c.z,
                    d.x, d.y, d.z);

                // Calculate the UVs. The u coordinates are a simple running
                // tally of the distance around the profile perimeter. The v1
                // coordinate is the negative of the "scalar projection" of the
                // plan's x1 coordinate onto the edge. The v2 coordinate is
                // the sum of the edge length and the x2 coordinate projected
                // onto the edge.
                const
                    u1 = perimeter,
                    u2 = u1 + profileEdgeLen,
                    v1 = -x1 * Math.cos(etheta - b1),
                    v2 = planEdgeLen + x2 * Math.cos(etheta - b2);

                k = PlaneMesh.write2(uv, k,
                    u1, v1, // a
                    u2, v1, // b
                    u2, v2, // c
                    u1, v2, // d
                );

                perimeter += profileEdgeLen;
                t1.reset(); t2.reset();
            }
        }

    }

}
